/**
 * A simple pub/sub mechanism for screen reader announcements
 * triggered by Formio component visibility changes.
 *
 * The {@link EhsFormioReactComponent} calls {@link announce} when a
 * conditionally hidden field becomes visible.
 * The form containers ({@link QuestionnaireContainer},
 * {@link FormioContainer}) subscribe via {@link onAnnouncement} and
 * render the message in an `aria-live` region.
 */

type Listener = (message: string) => void;

const listeners = new Set<Listener>();

/**
 * Subscribe to screen reader announcements.
 *
 * @param listener called with the announcement text
 * @returns an unsubscribe function
 */
export function onAnnouncement(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Publish a screen reader announcement to all subscribed containers.
 *
 * @param message the text to announce
 */
export function announce(message: string): void {
  listeners.forEach(l => l(message));
}
